import { useEffect, useRef, useCallback } from 'react'
import { MenuComponent } from '../../../assets/ts/components/MenuComponent'

/**
 * Header Menu Service Hook
 * 
 * This hook provides comprehensive header menu management,
 * including dropdown responsiveness monitoring and recovery mechanisms.
 */
export const useHeaderMenuService = () => {
  const retryCountRef = useRef<number>(0)
  const lastSuccessfulInteractionRef = useRef<number>(Date.now())
  const maxRetries = 3

  // Force reinitialize avatar dropdown
  const reinitializeAvatarDropdown = useCallback(() => {
    try {
      
      // Find avatar dropdown elements
      const avatarTrigger = document.querySelector('[data-kt-menu-trigger][data-kt-menu-placement="bottom-end"]') as HTMLElement
      const avatarMenu = document.querySelector('[data-kt-menu="true"].menu-sub-dropdown') as HTMLElement
      
      if (!avatarTrigger) {
        console.warn('⚠️ Avatar trigger element not found')
        return false
      }

      // Clean up existing menu instance
      const existingInstance = MenuComponent.getInstance(avatarTrigger)
      if (existingInstance) {
        try {
          // Hide any open dropdowns first
          existingInstance.hide(avatarTrigger)
        } catch (e) {
          console.log('')
        }
      }

      // Remove existing data attributes and classes
      avatarTrigger.classList.remove('show', 'menu-dropdown', 'hover', 'showing', 'hiding')
      if (avatarMenu) {
        avatarMenu.classList.remove('show')
      }

      // Force create new menu instance
      const newInstance = new MenuComponent(avatarMenu || avatarTrigger, {
        dropdown: {
          hoverTimeout: 200,
          zindex: 105,
        },
        accordion: {
          slideSpeed: 250,
          expand: false,
        },
      })

      // Test the new instance
      if (newInstance && typeof newInstance.show === 'function') {
        retryCountRef.current = 0
        lastSuccessfulInteractionRef.current = Date.now()
        return true
      }
      
      return false
    } catch (error) {
      console.error('❌ Error reinitializing avatar dropdown:', error)
      return false
    }
  }, [])

  // Test if avatar dropdown is responsive
  const testAvatarResponsiveness = useCallback(() => {
    try {
      const avatarTrigger = document.querySelector('[data-kt-menu-trigger][data-kt-menu-placement="bottom-end"]') as HTMLElement
      if (!avatarTrigger) {
        return false
      }

      const menuInstance = MenuComponent.getInstance(avatarTrigger)
      
      if (!menuInstance) {
        return false
      }

      // Test if methods are available and working
      const hasValidMethods = (
        typeof menuInstance.show === 'function' &&
        typeof menuInstance.hide === 'function' &&
        typeof menuInstance.getElement === 'function' &&
        menuInstance.getElement() !== null
      )

      if (!hasValidMethods) {
        return false
      }

      // Test if element is still in DOM and clickable
      const isInDOM = document.contains(avatarTrigger)
      const hasClickListener = avatarTrigger.hasAttribute('data-kt-menu-trigger')
      
      if (!isInDOM || !hasClickListener) {
        return false
      }

      return true
    } catch (error) {
      return false
    }
  }, [])

  // Enhanced click handler with fallback
  const handleAvatarClick = useCallback((event: Event) => {
    try {
      const target = event.target as HTMLElement
      const avatarTrigger = target.closest('[data-kt-menu-trigger][data-kt-menu-placement="bottom-end"]') as HTMLElement
      
      if (!avatarTrigger) {
        return
      }

      // Test responsiveness first
      if (!testAvatarResponsiveness()) {
        
        if (retryCountRef.current < maxRetries) {
          retryCountRef.current++
          
          // Attempt to reinitialize
          if (reinitializeAvatarDropdown()) {
            // Try to show the dropdown after reinitializing
            setTimeout(() => {
              const newInstance = MenuComponent.getInstance(avatarTrigger)
              if (newInstance) {
                newInstance.show(avatarTrigger)
              }
            }, 100)
          } else {
            console.error('❌ Failed to reinitialize avatar dropdown')
          }
        } else {
          console.error('❌ Max retries exceeded for avatar dropdown recovery')
          // Reset retry count after some time
          setTimeout(() => {
            retryCountRef.current = 0
          }, 30000)
        }
      } else {
        // Dropdown is responsive, record successful interaction
        lastSuccessfulInteractionRef.current = Date.now()
        retryCountRef.current = 0
      }
    } catch (error) {
      console.error('❌ Error in avatar click handler:', error)
    }
  }, [testAvatarResponsiveness, reinitializeAvatarDropdown])

  // Set up enhanced event listeners
  useEffect(() => {
    // Add enhanced click listener for avatar
    const avatarElements = document.querySelectorAll('[data-kt-menu-trigger][data-kt-menu-placement="bottom-end"]')
    
    avatarElements.forEach((element) => {
      element.addEventListener('click', handleAvatarClick, { capture: true })
    })

    // Periodic health check
    const healthCheckInterval = setInterval(() => {
      const timeSinceLastSuccess = Date.now() - lastSuccessfulInteractionRef.current
      
      // If it's been more than 2 minutes since last successful interaction, do a health check
      if (timeSinceLastSuccess > 2 * 60 * 1000) {
        if (!testAvatarResponsiveness()) {
          reinitializeAvatarDropdown()
        }
      }
    }, 60000) // Check every minute

    return () => {
      // Clean up event listeners
      avatarElements.forEach((element) => {
        element.removeEventListener('click', handleAvatarClick, { capture: true })
      })
      
      clearInterval(healthCheckInterval)
    }
  }, [handleAvatarClick, testAvatarResponsiveness, reinitializeAvatarDropdown])

  return {
    reinitializeAvatarDropdown,
    testAvatarResponsiveness,
    isHealthy: retryCountRef.current === 0
  }
}

/**
 * Header Menu Service Component
 * 
 * This component provides additional monitoring and recovery mechanisms 
 * for header menu functionality.
 */
const HeaderMenuService = () => {
  const { reinitializeAvatarDropdown, testAvatarResponsiveness } = useHeaderMenuService()

  // Window focus recovery
  useEffect(() => {
    const handleWindowFocus = () => {
      // When user comes back to the app, test avatar dropdown
      setTimeout(() => {
        if (!testAvatarResponsiveness()) {
          reinitializeAvatarDropdown()
        }
      }, 500)
    }

    window.addEventListener('focus', handleWindowFocus)
    
    return () => {
      window.removeEventListener('focus', handleWindowFocus)
    }
  }, [testAvatarResponsiveness, reinitializeAvatarDropdown])

  // This component doesn't render anything
  return null
}

export default HeaderMenuService
